import React, { useState, useEffect } from 'react';
import { PawPrint, Baby, Shield, Trash2, TrendingUp, Weight } from 'lucide-react';
import api from '../utils/api';

const CATEGORIAS_PT = {
  cordeiro: 'Cordeiro', borrego: 'Borrego',
  reprodutor: 'Reprodutor', matriz: 'Matriz', descarte: 'Descarte',
};

function KpiCard({ label, value, sub, color = '#2d6a4f', bg = '#dcfce7', icon: Icon }) {
  return (
    <div className="stat-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="stat-label">{label}</div>
          <div className="stat-value" style={{ color }}>{value ?? '—'}</div>
          {sub && <div className="stat-sub">{sub}</div>}
        </div>
        {Icon && (
          <div style={{ padding: 10, borderRadius: 10, background: bg }}>
            <Icon size={20} color={color} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function IndicadoresZootecnicos() {
  const [ind, setInd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/indicadores').then(r => setInd(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Carregando indicadores...</div>;
  if (!ind) return <div className="empty-state"><p>Erro ao carregar indicadores</p></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Indicadores Zootécnicos</h1>
          <p>Índices gerenciais do rebanho</p>
        </div>
      </div>

      {/* Rebanho reprodutivo */}
      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Rebanho Reprodutivo</h3>
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <KpiCard label="Total de Matrizes" value={ind.matrizes} icon={PawPrint} color="#EC4899" bg="#FCE7F3" />
        <KpiCard label="Total de Reprodutores" value={ind.reprodutores} icon={PawPrint} color="#2563EB" bg="#DBEAFE" />
        <KpiCard label="Matrizes Prenhes" value={ind.matrizes_prenhes} sub="com diagnóstico ativo" icon={PawPrint} color="#16A34A" bg="#DCFCE7" />
        <KpiCard label="Matrizes Vazias" value={ind.matrizes_vazias} sub="sem diagnóstico de prenhez" icon={PawPrint} color="#DC2626" bg="#FEE2E2" />
        <KpiCard
          label="Índice de Prenhez"
          value={ind.indice_prenhez != null ? `${ind.indice_prenhez}%` : '—'}
          sub={`${ind.prenhez_detalhes?.prenhas || 0} prenhas / ${ind.prenhez_detalhes?.total || 0} cobertas (12m)`}
          icon={TrendingUp} color="#7C3AED" bg="#EDE9FE"
        />
      </div>

      {/* Crias */}
      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Crias</h3>
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <KpiCard label="Em Amamentação" value={ind.cordeiros_amamentacao} sub="não desmamados, ativos" icon={Baby} color="#8B5CF6" bg="#EDE9FE" />
        <KpiCard label="Desmamados Ativos" value={ind.cordeiros_desmamados} sub="desmamados e ainda ativos" icon={Baby} color="#0891B2" bg="#CFFAFE" />
        <KpiCard
          label="Peso Médio ao Desmame"
          value={ind.peso_medio_desmame?.media != null ? `${ind.peso_medio_desmame.media} kg` : '—'}
          sub={`${ind.peso_medio_desmame?.total_registros || 0} desmames com peso registrado`}
          icon={Weight} color="#D97706" bg="#FEF3C7"
        />
      </div>

      {/* Saúde */}
      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Saúde & Gestão</h3>
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <KpiCard label="Em Tratamento" value={ind.em_tratamento} sub="carência ativa ou intervenção pendente" icon={Shield} color="#0891B2" bg="#CFFAFE" />
        <KpiCard label="Candidatos ao Descarte" value={ind.candidatos_descarte} sub="classificados em atenção ou candidato" icon={Trash2} color="#DC2626" bg="#FEE2E2" />
      </div>

      {/* Peso médio por categoria */}
      {ind.peso_medio_categoria?.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Peso Médio por Categoria</h3>
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <table>
              <thead>
                <tr><th>Categoria</th><th>Animais com pesagem</th><th>Peso Médio</th></tr>
              </thead>
              <tbody>
                {ind.peso_medio_categoria.map((r, i) => (
                  <tr key={i}>
                    <td style={{ textTransform: 'capitalize', fontWeight: 500 }}>{CATEGORIAS_PT[r.categoria] || r.categoria}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{r.qtd}</td>
                    <td><strong>{r.peso_medio} kg</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
